/**
 * @fileoverview Report Logs — AI-Powered Log Search & Browser
 * @description Full-featured SFTP log analysis with AI natural language search,
 *              data source browsing, report generation, and text search.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
  Collapse,
  Divider,
  LinearProgress,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  FolderOpen,
  FileText,
  Search,
  ArrowLeft,
  RefreshCw,
  Calendar,
  Download,
  Home,
  ChevronRight,
  ChevronDown,
  Eye,
  X,
  FolderTree,
  Play,
  CheckCircle2,
  XCircle,
  Terminal,
  Database,
  Layers,
  Sparkles,
  Send,
  Bot,
  User,
  Clock,
  AlertTriangle,
  Info,
  Hash,
  Plane,
  Users,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { reportLogsApi } from '../services/reportLogsApi';

/* ─── Helpers ─── */
const formatSize = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const INTEGRATOR_BASE = '/opt/jeppesen/jcms/tmp/integrator/requests';

/* ─── Example AI Queries ─── */
const EXAMPLE_QUERIES = [
  'Show me flight 2383 operations on May 11, 2026',
  'Find errors in tripstate for May 6, 2026',
  'Show ticket reservations for flight 454 today',
  'Find crew messages for crew ID 1084423 on May 11',
  'Show all flight operation errors on May 8, 2026',
  'Find roster imports for crew 1008879',
  'Show cancelled trips in tripstate today',
  'Find DH ticket cancellations for May 13 flights',
];

/* ─── Category Icons ─── */
const CATEGORY_ICONS = {
  'Ticket Reservations': Plane,
  'Crew Data': Users,
  'Flight Data': Plane,
  'Roster & Trips': Calendar,
  'Hotel Exports': Home,
  'Payroll': Hash,
  'Roster Output': FileText,
  'Errors': AlertTriangle,
};

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const ReportLogs = () => {
  const [activeTab, setActiveTab] = useState(0);

  /* ─── AI Search State ─── */
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  const chatEndRef = useRef(null);

  /* ─── SFTP Environment ─── */
  const [sftpEnv, setSftpEnv] = useState('test');

  /* ─── Report Generation State ─── */
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedReport, setSelectedReport] = useState('');
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);

  /* ─── Data Sources State ─── */
  const [dataSources, setDataSources] = useState(null);
  const [loadingSources, setLoadingSources] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceDate, setSourceDate] = useState('');
  const [sourceFiles, setSourceFiles] = useState(null);
  const [loadingSourceFiles, setLoadingSourceFiles] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [textSearchQuery, setTextSearchQuery] = useState('');
  const [textSearchPath, setTextSearchPath] = useState('');
  const [textSearchResults, setTextSearchResults] = useState(null);
  const [textSearching, setTextSearching] = useState(false);

  /* ─── File Browser State ─── */
  const [currentPath, setCurrentPath] = useState(INTEGRATOR_BASE);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ─── File Viewer ─── */
  const [viewingFile, setViewingFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [loadingFile, setLoadingFile] = useState(false);

  /* ─── Global Error ─── */
  const [error, setError] = useState(null);

  /* ─── Scroll chat to bottom ─── */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiHistory]);

  /* ─── Load report types on mount ─── */
  useEffect(() => {
    reportLogsApi
      .getReportTypes()
      .then((data) => {
        setReportTypes(data.reports || []);
        if (data.reports?.length > 0) setSelectedReport(data.reports[0].id);
      })
      .catch(() => {});
  }, []);

  /* ─────────────────────────────────────
     AI Search
     ───────────────────────────────────── */
  const handleAiSearch = useCallback(
    async (queryOverride) => {
      const q = (queryOverride || aiQuery).trim();
      if (!q) return;

      setAiLoading(true);
      setAiQuery('');

      // Add user message
      const userMsg = { role: 'user', content: q, timestamp: new Date() };
      setAiHistory((prev) => [...prev, userMsg]);

      try {
        const data = await reportLogsApi.aiSearch(q, true, sftpEnv);

        const botMsg = {
          role: 'assistant',
          content: data.summary || 'Search completed.',
          intent: data.intent,
          results: data.results || [],
          totalFiles: data.total_files,
          totalMatches: data.total_matches,
          searchedFolders: data.searched_folders,
          searchedDate: data.searched_date,
          reportFilename: null,
          reportAvailable: data.report_available || false,
          env: data.env || sftpEnv,
          query: q,
          timestamp: new Date(),
        };
        setAiHistory((prev) => [...prev, botMsg]);
      } catch (err) {
        const errorMsg = {
          role: 'assistant',
          content: `Search failed: ${err.message || 'Unknown error'}`,
          isError: true,
          timestamp: new Date(),
        };
        setAiHistory((prev) => [...prev, errorMsg]);
      } finally {
        setAiLoading(false);
      }
    },
    [aiQuery, sftpEnv]
  );

  /* ─── On-demand report generation ─── */
  const handleGenerateReport = useCallback(async (msgIdx) => {
    const msg = aiHistory[msgIdx];
    if (!msg || !msg.results?.length) return;

    // Mark as generating
    setAiHistory((prev) =>
      prev.map((m, i) => (i === msgIdx ? { ...m, reportGenerating: true } : m))
    );

    try {
      const data = await reportLogsApi.generateAiReport({
        results: msg.results,
        query: msg.query || '',
        searched_date: msg.searchedDate,
        searched_folders: msg.searchedFolders,
      }, msg.env || sftpEnv);

      // Download CSV directly from response content (no SFTP round-trip)
      if (data.csv_content) {
        const blob = new Blob([data.csv_content], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || 'ai_report.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }

      setAiHistory((prev) =>
        prev.map((m, i) =>
          i === msgIdx
            ? { ...m, reportFilename: data.filename, reportGenerating: false, reportAvailable: false }
            : m
        )
      );
    } catch (err) {
      setAiHistory((prev) =>
        prev.map((m, i) =>
          i === msgIdx
            ? { ...m, reportGenerating: false, reportError: err.message || 'Failed to generate report' }
            : m
        )
      );
    }
  }, [aiHistory]);

  /* ─────────────────────────────────────
     File Operations
     ───────────────────────────────────── */
  const handleViewFile = useCallback(async (filePath) => {
    setLoadingFile(true);
    setViewingFile(filePath);
    try {
      const data = await reportLogsApi.fetchFile(filePath, sftpEnv);
      setFileContent(data.content || '');
    } catch (err) {
      setFileContent(`Error loading file: ${err.message}`);
    } finally {
      setLoadingFile(false);
    }
  }, [sftpEnv]);

  const handleDownload = useCallback(async (filePath, filename) => {
    try {
      const blob = await reportLogsApi.downloadReport(filePath, filename, sftpEnv);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || filePath.split('/').pop() || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  }, [sftpEnv]);

  const closeFileViewer = () => {
    setViewingFile(null);
    setFileContent('');
  };

  /* ─────────────────────────────────────
     Report Execution
     ───────────────────────────────────── */
  const handleExecuteReport = useCallback(async () => {
    if (!selectedReport || !reportStartDate || !reportEndDate) return;
    if (reportEndDate < reportStartDate) {
      setError('End date must be on or after the start date.');
      return;
    }
    setExecuting(true);
    setExecutionResult(null);
    setError(null);
    try {
      const result = await reportLogsApi.executeReport(
        selectedReport,
        reportStartDate,
        reportEndDate,
        sftpEnv
      );
      setExecutionResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  }, [selectedReport, reportStartDate, reportEndDate, sftpEnv]);

  /* ─────────────────────────────────────
     Data Sources
     ───────────────────────────────────── */
  const loadDataSources = useCallback(async () => {
    setLoadingSources(true);
    try {
      const data = await reportLogsApi.getDataSources(sftpEnv);
      setDataSources(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSources(false);
    }
  }, [sftpEnv]);

  const loadSourceFiles = useCallback(async (sourceId, date) => {
    setLoadingSourceFiles(true);
    setSourceFiles(null);
    try {
      const data = await reportLogsApi.getSourceFiles(sourceId, date, sftpEnv);
      setSourceFiles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSourceFiles(false);
    }
  }, [sftpEnv]);

  const handleTextSearch = useCallback(async () => {
    if (!textSearchQuery.trim() || !textSearchPath.trim()) return;
    setTextSearching(true);
    setTextSearchResults(null);
    try {
      const data = await reportLogsApi.searchText(textSearchQuery, textSearchPath, 100, sftpEnv);
      setTextSearchResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setTextSearching(false);
    }
  }, [textSearchQuery, textSearchPath, sftpEnv]);

  /* ─────────────────────────────────────
     File Browser
     ───────────────────────────────────── */
  const loadDirectory = useCallback(async (path = '/') => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportLogsApi.listDirectory(path, sftpEnv);
      setEntries(data.entries || []);
      setCurrentPath(path);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sftpEnv]);

  const navigateTo = useCallback(
    (name) => {
      const newPath = currentPath === '/' ? `/${name}` : `${currentPath}/${name}`;
      loadDirectory(newPath);
    },
    [currentPath, loadDirectory]
  );

  const navigateUp = useCallback(() => {
    if (currentPath === INTEGRATOR_BASE || currentPath === '/') return;
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = `/${parts.join('/')}`;
    // Don't navigate above INTEGRATOR_BASE
    if (newPath.length < INTEGRATOR_BASE.length) {
      loadDirectory(INTEGRATOR_BASE);
    } else {
      loadDirectory(newPath);
    }
  }, [currentPath, loadDirectory]);

  /** Display-friendly path: strip the INTEGRATOR_BASE prefix */
  const displayPath = (fullPath) => {
    if (!fullPath) return '';
    if (fullPath.startsWith(INTEGRATOR_BASE + '/')) {
      return fullPath.slice(INTEGRATOR_BASE.length + 1);
    }
    if (fullPath === INTEGRATOR_BASE) return '';
    return fullPath;
  };

  const pathParts = displayPath(currentPath).split('/').filter(Boolean);
  const breadcrumbs = [
    { label: 'requests', path: INTEGRATOR_BASE },
    ...pathParts.map((part, i) => ({
      label: part,
      path: `${INTEGRATOR_BASE}/${pathParts.slice(0, i + 1).join('/')}`,
    })),
  ];

  const selectedReportObj = reportTypes.find((r) => r.id === selectedReport);

  /* ═══════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════ */
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      {/* ─── Header ─── */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Sparkles size={20} color="#fff" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#01426A', lineHeight: 1.2 }}>
            Report Logs
          </Typography>
          <Typography variant="caption" color="text.secondary">
            AI-powered log search &amp; analysis
          </Typography>
        </Box>
      </Box>

      {/* ─── Tabs + Env Toggle ─── */}
      <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }} elevation={0} variant="outlined">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
              if (v === 2) {
                if (!dataSources) loadDataSources();
                if (entries.length === 0) loadDirectory(currentPath);
              }
            }}
            sx={{
              flex: 1,
              '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', py: 1, minHeight: 42 },
              '& .Mui-selected': { color: '#01426A' },
              '& .MuiTabs-indicator': { bgcolor: '#0097A9', height: 3 },
            }}
          >
            <Tab label="AI Search" icon={<Sparkles size={16} />} iconPosition="start" />
            <Tab label="Generate Reports" icon={<Play size={16} />} iconPosition="start" />
            <Tab label="Browse & Search" icon={<FolderOpen size={16} />} iconPosition="start" />
          </Tabs>

          <Divider orientation="vertical" flexItem sx={{ my: 0.75 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5 }}>
            <Database size={14} color="#64748b" />
            <ToggleButtonGroup
              value={sftpEnv}
              exclusive
              onChange={(_, v) => { if (v) setSftpEnv(v); }}
              size="small"
              sx={{
                height: 30,
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  px: 1.5,
                  py: 0,
                  lineHeight: 1,
                  border: '1px solid #cbd5e1',
                  color: '#64748b',
                  '&.Mui-selected': {
                    bgcolor: '#01426A',
                    color: '#fff',
                    borderColor: '#01426A',
                    '&:hover': { bgcolor: '#012d49' },
                  },
                  '&:hover': { bgcolor: 'rgba(1,66,106,0.06)' },
                },
              }}
            >
              <ToggleButton value="test">Test</ToggleButton>
              <ToggleButton value="cert">Cert</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ═══════════════════════════════════
          TAB 0 — AI Search
          ═══════════════════════════════════ */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 260px)', minHeight: 400 }}>
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {aiHistory.length === 0 ? (
              /* ── Empty / Welcome State — centered input like Gemini ── */
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 3,
                  gap: 3,
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.5,
                    }}
                  >
                    What can I help you find?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                    Search flight ops, trips, rosters, tickets, crew messages &amp; errors
                  </Typography>
                </Box>

                {/* ── Centered Input Pill ── */}
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 680,
                    bgcolor: '#f0f4f9',
                    borderRadius: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 50,
                    px: 2,
                    transition: 'background 0.2s, box-shadow 0.2s',
                    '&:focus-within': {
                      bgcolor: '#fff',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <Sparkles size={18} style={{ color: '#0097A9', flexShrink: 0, marginRight: 10 }} />
                  <input
                    type="text"
                    placeholder="Ask about logs..."
                    aria-label="AI log search query"
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAiSearch();
                      }
                    }}
                    disabled={aiLoading}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: '0.9rem',
                      lineHeight: '24px',
                      color: '#1f1f1f',
                      fontFamily: 'inherit',
                    }}
                  />
                  <IconButton
                    onClick={() => handleAiSearch()}
                    disabled={!aiQuery.trim() || aiLoading}
                    size="small"
                    sx={{
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      ml: 0.5,
                      bgcolor: aiQuery.trim() && !aiLoading ? '#01426A' : 'transparent',
                      color: aiQuery.trim() && !aiLoading ? '#fff' : '#70757a',
                      borderRadius: '50%',
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: aiQuery.trim() && !aiLoading ? '#012d49' : '#e8eaed' },
                      '&.Mui-disabled': { color: '#bdc1c6' },
                    }}
                  >
                    {aiLoading ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
                  </IconButton>
                </Box>

                {/* ── Suggestion Chips ── */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', maxWidth: 680 }}>
                  {EXAMPLE_QUERIES.map((eq, i) => (
                    <Chip
                      key={i}
                      label={eq}
                      variant="outlined"
                      onClick={() => handleAiSearch(eq)}
                      size="small"
                      sx={{
                        cursor: 'pointer',
                        borderColor: '#e2e8f0',
                        '&:hover': { bgcolor: '#f0f9ff', borderColor: '#0097A9' },
                        fontSize: '0.75rem',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            ) : (
              /* ── Has Messages — chat layout with bottom input ── */
              <>
                {/* ── Messages Area ── */}
                <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1.5, md: 2.5 } }}>
                <Stack spacing={2.5}>
                  {aiHistory.map((msg, idx) => (
                    <Fade in key={idx}>
                      <Box>
                        {msg.role === 'user' ? (
                          /* ── User Bubble ── */
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                            <Box
                              sx={{
                                maxWidth: '70%',
                                bgcolor: '#01426A',
                                color: '#fff',
                                px: 2.5,
                                py: 1.5,
                                borderRadius: '20px 20px 4px 20px',
                              }}
                            >
                              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{msg.content}</Typography>
                              <Typography variant="caption" sx={{ opacity: 0.5, mt: 0.5, display: 'block', textAlign: 'right' }}>
                                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          /* ── Assistant Response ── */
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                background: msg.isError
                                  ? 'linear-gradient(135deg, #dc2626, #f97316)'
                                  : 'linear-gradient(135deg, #01426A, #0097A9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.3,
                              }}
                            >
                              <Bot size={15} color="#fff" />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {/* Search metadata bar */}
                              {msg.intent && !msg.isError && (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                  <Chip
                                    icon={<FolderOpen size={11} />}
                                    label={msg.searchedFolders?.join(', ')}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.68rem', height: 22, borderColor: '#d1d5db' }}
                                  />
                                  <Chip
                                    icon={<Calendar size={11} />}
                                    label={msg.searchedDate}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.68rem', height: 22, borderColor: '#d1d5db' }}
                                  />
                                  <Chip
                                    icon={<FileText size={11} />}
                                    label={`${msg.totalFiles} file${msg.totalFiles !== 1 ? 's' : ''} \u00b7 ${msg.totalMatches} match${msg.totalMatches !== 1 ? 'es' : ''}`}
                                    size="small"
                                    variant="outlined"
                                    color={msg.totalFiles > 0 ? 'success' : 'default'}
                                    sx={{ fontSize: '0.68rem', height: 22 }}
                                  />
                                  <Chip
                                    icon={<Database size={11} />}
                                    label={msg.env === 'cert' ? 'CERT' : 'TEST'}
                                    size="small"
                                    variant="filled"
                                    sx={{
                                      fontSize: '0.68rem',
                                      height: 22,
                                      fontWeight: 700,
                                      bgcolor: msg.env === 'cert' ? '#0097A9' : '#01426A',
                                      color: '#fff',
                                    }}
                                  />
                                </Box>
                              )}

                              {/* Summary text */}
                              <Paper
                                elevation={0}
                                sx={{
                                  px: 2.5,
                                  py: 2,
                                  bgcolor: msg.isError ? '#fef2f2' : '#f8fafc',
                                  borderRadius: '4px 16px 16px 16px',
                                  border: '1px solid',
                                  borderColor: msg.isError ? '#fecaca' : '#e2e8f0',
                                }}
                              >
                                <Box
                                  sx={{
                                    lineHeight: 1.7,
                                    color: '#1e293b',
                                    fontSize: '0.875rem',
                                    '& h1, & h2, & h3, & h4': {
                                      fontSize: '0.95rem',
                                      fontWeight: 600,
                                      mt: 1.5,
                                      mb: 0.5,
                                      color: '#0f172a',
                                    },
                                    '& h3': { fontSize: '0.9rem' },
                                    '& p': { my: 0.5 },
                                    '& ul, & ol': { pl: 2.5, my: 0.5 },
                                    '& li': { mb: 0.3 },
                                    '& strong': { fontWeight: 600 },
                                    '& code': {
                                      fontFamily: 'monospace',
                                      bgcolor: '#f1f5f9',
                                      px: 0.5,
                                      borderRadius: 0.5,
                                      fontSize: '0.8rem',
                                    },
                                    '& hr': { my: 1.5, borderColor: '#e2e8f0' },
                                  }}
                                >
                                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                                </Box>
                              </Paper>

                              {/* File results — collapsible list */}
                              {msg.results?.length > 0 && (
                                <Box sx={{ mt: 1.5 }}>
                                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block', pl: 0.5 }}>
                                    Matched Files ({msg.results.length})
                                  </Typography>
                                  <Box
                                    sx={{
                                      border: '1px solid #e5e7eb',
                                      borderRadius: 1.5,
                                      overflow: 'hidden',
                                      maxHeight: 320,
                                      overflowY: 'auto',
                                    }}
                                  >
                                    {msg.results.slice(0, 20).map((fileResult, fIdx) => (
                                      <Box
                                        key={fIdx}
                                        sx={{
                                          px: 1.5,
                                          py: 0.75,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          borderBottom: fIdx < Math.min(msg.results.length, 20) - 1 ? '1px solid #f3f4f6' : 'none',
                                          cursor: 'pointer',
                                          '&:hover': { bgcolor: '#f0f9ff' },
                                          transition: 'background 0.15s',
                                        }}
                                        onClick={() => handleViewFile(fileResult.file)}
                                      >
                                        <FileText size={13} style={{ color: '#0097A9', flexShrink: 0 }} />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            flex: 1,
                                            fontSize: '0.78rem',
                                            fontFamily: 'monospace',
                                            color: '#334155',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {fileResult.filename}
                                        </Typography>
                                        {fileResult.modified && (
                                          <Tooltip title="Last modified">
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: '#64748b',
                                                fontSize: '0.68rem',
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                              }}
                                            >
                                              {new Date(fileResult.modified).toLocaleString()}
                                            </Typography>
                                          </Tooltip>
                                        )}
                                        {fileResult.folder && (
                                          <Chip
                                            label={fileResult.folder}
                                            size="small"
                                            sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f1f5f9', color: '#64748b' }}
                                          />
                                        )}
                                        <Tooltip title="View">
                                          <IconButton size="small" sx={{ p: 0.3 }}>
                                            <Eye size={13} color="#64748b" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download">
                                          <IconButton
                                            size="small"
                                            sx={{ p: 0.3 }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownload(fileResult.file, fileResult.filename);
                                            }}
                                          >
                                            <Download size={13} color="#64748b" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    ))}
                                  </Box>
                                  {msg.results.length > 20 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', pl: 0.5 }}>
                                      + {msg.results.length - 20} more files
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {/* Download report — at the BOTTOM after files */}
                              {msg.reportFilename && (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    p: 1.5,
                                    bgcolor: '#f0fdfa',
                                    border: '1px solid #99f6e4',
                                    borderRadius: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                  }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#0f766e', fontSize: '0.82rem' }}>
                                      Report Downloaded
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {msg.reportFilename}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              {/* Generate Report button — shown for list-all queries */}
                              {!msg.reportFilename && msg.reportAvailable && (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    p: 1.5,
                                    bgcolor: '#eff6ff',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                  }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#1e40af', fontSize: '0.82rem' }}>
                                      Export as CSV Report
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Generate a consolidated report with all {msg.totalFiles} records
                                    </Typography>
                                  </Box>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={msg.reportGenerating ? <CircularProgress size={14} color="inherit" /> : <FileText size={14} />}
                                    disabled={msg.reportGenerating}
                                    onClick={() => handleGenerateReport(idx)}
                                    sx={{
                                      borderRadius: 2,
                                      bgcolor: '#01426A',
                                      '&:hover': { bgcolor: '#012d49' },
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      fontSize: '0.78rem',
                                      px: 2,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {msg.reportGenerating ? 'Generating...' : 'Generate Report'}
                                  </Button>
                                </Box>
                              )}

                              {/* Report generation error */}
                              {msg.reportError && (
                                <Alert severity="error" sx={{ mt: 1, borderRadius: 1.5, fontSize: '0.78rem' }}>
                                  {msg.reportError}
                                </Alert>
                              )}

                              {/* Timestamp */}
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', pl: 0.5 }}>
                                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Fade>
                  ))}

                  {/* Loading indicator */}
                  {aiLoading && (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #01426A, #0097A9)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Bot size={15} color="#fff" />
                      </Box>
                      <Paper
                        elevation={0}
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          bgcolor: '#f8fafc',
                          borderRadius: '4px 16px 16px 16px',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CircularProgress size={14} sx={{ color: '#0097A9' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                            Searching logs &amp; analyzing results...
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  )}

                  <div ref={chatEndRef} />
                </Stack>
                </Box>

                {/* ── Bottom Input Bar ── */}
                <Box sx={{ px: 2, pt: 1, pb: 1.5, borderTop: '1px solid #e8eaed', bgcolor: '#fff' }}>
              <Box
                sx={{
                  maxWidth: 800,
                  mx: 'auto',
                  bgcolor: '#f0f4f9',
                  borderRadius: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 48,
                  px: 2,
                  transition: 'background 0.2s, box-shadow 0.2s',
                  '&:focus-within': {
                    bgcolor: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <Sparkles size={18} style={{ color: '#0097A9', flexShrink: 0, marginRight: 10 }} />
                <input
                  type="text"
                  placeholder="Ask about logs..."
                  aria-label="AI log search query"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAiSearch();
                    }
                  }}
                  disabled={aiLoading}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '0.9rem',
                    lineHeight: '24px',
                    color: '#1f1f1f',
                    fontFamily: 'inherit',
                  }}
                />
                <IconButton
                  onClick={() => handleAiSearch()}
                  disabled={!aiQuery.trim() || aiLoading}
                  size="small"
                  sx={{
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    ml: 0.5,
                    bgcolor: aiQuery.trim() && !aiLoading ? '#01426A' : 'transparent',
                    color: aiQuery.trim() && !aiLoading ? '#fff' : '#70757a',
                    borderRadius: '50%',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: aiQuery.trim() && !aiLoading ? '#012d49' : '#e8eaed' },
                    '&.Mui-disabled': { color: '#bdc1c6' },
                  }}
                >
                  {aiLoading ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
                </IconButton>
              </Box>
            </Box>
              </>
            )}
          </Paper>
        </Box>
      )}

      {/* ═══════════════════════════════════
          TAB 1 — Generate Reports
          ═══════════════════════════════════ */}
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600, color: '#01426A' }}>
                Generate Report
              </Typography>
              <Stack spacing={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    value={selectedReport}
                    onChange={(e) => {
                      setSelectedReport(e.target.value);
                      setExecutionResult(null);
                    }}
                    label="Report Type"
                  >
                    {reportTypes.map((rt) => (
                      <MenuItem key={rt.id} value={rt.id}>{rt.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedReportObj && (
                  <Alert severity="info" icon={<Info size={18} />} sx={{ borderRadius: 1.5 }}>
                    {selectedReportObj.description}
                  </Alert>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={reportStartDate}
                    onChange={(e) => {
                      const value = e.target.value;
                      setReportStartDate(value);
                      // Keep the range valid: pull end date forward when it precedes start.
                      setReportEndDate((prev) => (prev && prev < value ? value : prev));
                      setExecutionResult(null);
                    }}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': 'Start Date' }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={reportEndDate}
                    onChange={(e) => {
                      setReportEndDate(e.target.value);
                      setExecutionResult(null);
                    }}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: reportStartDate || undefined, 'aria-label': 'End Date' }}
                  />
                </Stack>

                {reportStartDate && reportEndDate && reportEndDate < reportStartDate && (
                  <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                    End date must be on or after the start date.
                  </Alert>
                )}

                {selectedReportObj && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Steps:
                    </Typography>
                    {selectedReportObj.commands.map((cmd, idx) => (
                      <Chip key={cmd.id} label={`${idx + 1}. ${cmd.label}`} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                )}

                <Button
                  variant="contained"
                  size="large"
                  startIcon={executing ? <CircularProgress size={18} color="inherit" /> : <Play size={18} />}
                  onClick={handleExecuteReport}
                  disabled={
                    !selectedReport ||
                    !reportStartDate ||
                    !reportEndDate ||
                    reportEndDate < reportStartDate ||
                    executing
                  }
                  sx={{ fontWeight: 600, borderRadius: 2, bgcolor: '#01426A', '&:hover': { bgcolor: '#012d49' } }}
                >
                  {executing ? 'Executing...' : 'Execute Report'}
                </Button>
              </Stack>
            </Paper>

            {/* Results */}
            {executionResult && (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#01426A' }}>
                  Results — {executionResult.report_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {executionResult.start_date && executionResult.end_date
                    ? (executionResult.start_date === executionResult.end_date
                        ? `Date: ${executionResult.start_date}`
                        : `Date range: ${executionResult.start_date} → ${executionResult.end_date}`)
                    : `Date: ${executionResult.date}`}
                </Typography>
                <Stack spacing={2}>
                  {executionResult.results.map((result) => (
                    <Paper
                      key={result.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 1.5,
                        borderColor: result.success ? '#86efac' : '#fca5a5',
                        bgcolor: result.success ? '#f0fdf4' : '#fef2f2',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {result.success ? (
                          <CheckCircle2 size={18} style={{ color: '#16a34a' }} />
                        ) : (
                          <XCircle size={18} style={{ color: '#dc2626' }} />
                        )}
                        <Typography variant="subtitle2" fontWeight={600}>{result.label}</Typography>
                        <Chip
                          label={`exit: ${result.exit_code}`}
                          size="small"
                          color={result.success ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </Box>
                      {result.stdout && (
                        <Box sx={{ bgcolor: '#1e293b', color: '#e2e8f0', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', mb: 1, whiteSpace: 'pre-wrap' }}>
                          <Terminal size={12} style={{ marginRight: 6, opacity: 0.6 }} />
                          {result.stdout}
                        </Box>
                      )}
                      {result.stderr && (
                        <Box sx={{ bgcolor: '#fef2f2', color: '#dc2626', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', mb: 1, whiteSpace: 'pre-wrap' }}>
                          {result.stderr}
                        </Box>
                      )}
                      {result.success && result.output_file && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button size="small" variant="contained" startIcon={<Download size={14} />} onClick={() => handleDownload(result.output_file, result.output_file.split('/').pop())} sx={{ borderRadius: 1.5 }}>
                            Download
                          </Button>
                          <Button size="small" variant="outlined" startIcon={<Eye size={14} />} onClick={() => handleViewFile(result.output_file)} sx={{ borderRadius: 1.5 }}>
                            View
                          </Button>
                        </Stack>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            )}
          </Box>
        </Box>
      )}

      {/* ═══════════════════════════════════
          TAB 2 — Browse & Search
          ═══════════════════════════════════ */}
      {activeTab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Text Search Bar */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <TextField
                label="Search Text"
                placeholder="e.g. breadcrumbId, flight_number, error..."
                value={textSearchQuery}
                onChange={(e) => setTextSearchQuery(e.target.value)}
                size="small"
                sx={{ minWidth: 250, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                onKeyDown={(e) => e.key === 'Enter' && handleTextSearch()}
              />
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                in: {displayPath(textSearchPath) || 'Select a source →'}
              </Typography>
              <Button
                variant="contained"
                startIcon={textSearching ? <CircularProgress size={16} color="inherit" /> : <Search size={16} />}
                onClick={handleTextSearch}
                disabled={!textSearchQuery.trim() || !textSearchPath.trim() || textSearching}
                sx={{ whiteSpace: 'nowrap', minWidth: 100, borderRadius: 1.5, bgcolor: '#01426A', '&:hover': { bgcolor: '#012d49' } }}
              >
                {textSearching ? 'Searching...' : 'Search'}
              </Button>
              {textSearchResults && (
                <Chip
                  label={`${textSearchResults.total_matches} matches in ${textSearchResults.total_files} files`}
                  color={textSearchResults.total_matches > 0 ? 'success' : 'default'}
                  size="small"
                  onDelete={() => setTextSearchResults(null)}
                />
              )}
            </Stack>
          </Paper>

          {/* Text Search Results */}
          {textSearchResults?.total_files > 0 && (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Search size={18} style={{ color: '#01426A' }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                  Results for &quot;{textSearchResults.query}&quot;
                </Typography>
                <Chip label={`${textSearchResults.total_matches} matches`} size="small" color="primary" variant="outlined" />
              </Box>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {textSearchResults.results.map((fileResult, fIdx) => (
                  <Box key={fIdx}>
                    <Box
                      sx={{ px: 2, py: 1, bgcolor: '#fafbfc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { bgcolor: '#f0f9ff' } }}
                      onClick={() => handleViewFile(fileResult.file)}
                    >
                      <FileText size={14} style={{ color: '#0097A9' }} />
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} noWrap>{fileResult.filename}</Typography>
                      {fileResult.modified && (
                        <Tooltip title="Last modified">
                          <Typography variant="caption" sx={{ color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {new Date(fileResult.modified).toLocaleString()}
                          </Typography>
                        </Tooltip>
                      )}
                      <Chip label={`${fileResult.matches.length} hits`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                      <Tooltip title="View"><IconButton size="small"><Eye size={14} /></IconButton></Tooltip>
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownload(fileResult.file, fileResult.filename); }}>
                          <Download size={14} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {fileResult.matches.map((m, mIdx) => (
                      <Box key={mIdx} sx={{ px: 2, pl: 5, py: 0.5, borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace', fontSize: '0.72rem', color: '#475569', display: 'flex', gap: 1 }}>
                        <Typography component="span" sx={{ color: '#94a3b8', minWidth: 36, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.72rem', flexShrink: 0 }}>L{m.line}</Typography>
                        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all' }}>{m.snippet}</Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {textSearchResults?.total_files === 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>No matches found for &quot;{textSearchResults.query}&quot;</Alert>
          )}

          {/* Main layout: Source tree (left) + File browser (right) */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left panel: Source tree */}
            <Paper variant="outlined" sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Database size={16} style={{ color: '#01426A' }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>Data Sources</Typography>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={loadDataSources} disabled={loadingSources}>
                    <RefreshCw size={14} className={loadingSources ? 'spin' : ''} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {loadingSources ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : dataSources ? (
                  Object.entries(dataSources.categories).map(([category, sources]) => {
                    const CatIcon = CATEGORY_ICONS[category] || Database;
                    return (
                      <Box key={category}>
                        <Box
                          sx={{ px: 1.5, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { bgcolor: '#f1f5f9' } }}
                          onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                        >
                          <CatIcon size={13} style={{ color: '#01426A' }} />
                          {expandedCategory === category ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          <Typography variant="caption" fontWeight={700} color="#01426A" sx={{ flex: 1 }}>{category}</Typography>
                          <Chip label={sources.length} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                        </Box>
                        <Collapse in={expandedCategory === category}>
                          {sources.map((source) => (
                            <Box
                              key={source.id}
                              sx={{
                                px: 1.5, py: 1, pl: 3.5, borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                                bgcolor: selectedSource?.id === source.id ? '#eff6ff' : 'transparent',
                                '&:hover': { bgcolor: '#f0f9ff' },
                              }}
                              onClick={() => {
                                setSelectedSource(source);
                                setSourceFiles(null);
                                setSourceDate('');
                                const sourcePath = `${INTEGRATOR_BASE}/${source.id}`;
                                setTextSearchPath(sourcePath);
                                loadDirectory(sourcePath);
                              }}
                            >
                              <Typography variant="caption" fontWeight={500} sx={{ display: 'block' }}>{source.name}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', lineHeight: 1.2 }}>{source.description}</Typography>
                            </Box>
                          ))}
                        </Collapse>
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Database size={28} style={{ color: '#9ca3af' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Click refresh to load</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Right panel: File browser */}
            <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, borderRadius: 2, overflow: 'hidden' }}>
              {/* Toolbar */}
              <Box sx={{ p: 1, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Tooltip title="Go up">
                  <span>
                    <IconButton size="small" onClick={navigateUp} disabled={currentPath === INTEGRATOR_BASE || loading}>
                      <ArrowLeft size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={() => loadDirectory(currentPath)} disabled={loading}>
                    <RefreshCw size={16} className={loading ? 'spin' : ''} />
                  </IconButton>
                </Tooltip>
                <Breadcrumbs separator={<ChevronRight size={12} />} sx={{ flex: 1, ml: 0.5 }}>
                  {breadcrumbs.map((bc, i) => {
                    const isLast = i === breadcrumbs.length - 1;
                    return isLast ? (
                      <Typography key={bc.path} variant="caption" fontWeight={600} noWrap>{bc.label}</Typography>
                    ) : (
                      <Link key={bc.path} component="button" variant="caption" underline="hover" onClick={() => loadDirectory(bc.path)} sx={{ cursor: 'pointer' }}>
                        {bc.label === 'requests' ? <Home size={13} /> : bc.label}
                      </Link>
                    );
                  })}
                </Breadcrumbs>
                {/* Date quick-jump dropdown — visible when a source with dates is selected */}
                {selectedSource?.available_dates?.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontSize: '0.8rem' }}>Jump to Date</InputLabel>
                    <Select
                      value={sourceDate}
                      onChange={(e) => {
                        const d = e.target.value;
                        setSourceDate(d);
                        const datePath = `${INTEGRATOR_BASE}/${selectedSource.id}/${d}`;
                        setTextSearchPath(datePath);
                        loadDirectory(datePath);
                      }}
                      label="Jump to Date"
                      sx={{ fontSize: '0.8rem', height: 32 }}
                    >
                      {selectedSource.available_dates.map((d) => (
                        <MenuItem key={d} value={d} sx={{ fontSize: '0.8rem' }}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              {/* File list */}
              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {loading ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">Loading...</Typography>
                  </Box>
                ) : entries.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <FolderTree size={28} style={{ color: '#9ca3af' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {currentPath === INTEGRATOR_BASE ? 'Select a data source or click refresh' : 'Empty directory'}
                    </Typography>
                  </Box>
                ) : (
                  entries.map((entry, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex', alignItems: 'center', px: 2, py: 0.75, gap: 1.5,
                        borderBottom: '1px solid #f3f4f6',
                        '&:hover': { bgcolor: entry.is_dir ? '#fffbeb' : '#f0f9ff' },
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        if (entry.is_dir) {
                          const newPath = `${currentPath}/${entry.name}`;
                          loadDirectory(newPath);
                          setTextSearchPath(newPath);
                        } else {
                          handleViewFile(`${currentPath}/${entry.name}`);
                        }
                      }}
                    >
                      {entry.is_dir ? (
                        <FolderOpen size={16} style={{ color: '#f59e0b' }} />
                      ) : (
                        <FileText size={16} style={{ color: '#0097A9' }} />
                      )}
                      <Typography variant="body2" fontWeight={entry.is_dir ? 600 : 400} sx={{ flex: 1 }} noWrap>{entry.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
                        {entry.is_dir ? '—' : formatSize(entry.size)}
                      </Typography>
                      {entry.modified && (
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, textAlign: 'right' }}>
                          {new Date(entry.modified).toLocaleString()}
                        </Typography>
                      )}
                      {!entry.is_dir && (
                        <>
                          <Tooltip title="View"><IconButton size="small"><Eye size={14} /></IconButton></Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownload(`${currentPath}/${entry.name}`, entry.name); }}>
                              <Download size={14} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      )}

      {/* ═══════════════════════════════════
          File Viewer Modal
          ═══════════════════════════════════ */}
      {viewingFile && (
        <Paper
          sx={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 1300, display: 'flex', flexDirection: 'column', borderRadius: 0,
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#01426A', color: '#fff' }}>
            <FileText size={20} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>{viewingFile.split('/').pop()}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>{displayPath(viewingFile)}</Typography>
            </Box>
            <Tooltip title="Download">
              <IconButton sx={{ color: '#fff' }} onClick={() => handleDownload(viewingFile, viewingFile.split('/').pop())}>
                <Download size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton onClick={closeFileViewer} sx={{ color: '#fff' }}>
                <X size={20} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loadingFile ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }} color="text.secondary">Loading...</Typography>
              </Box>
            ) : (
              <Box
                component="pre"
                sx={{
                  m: 0, p: 2,
                  fontFamily: '"Cascadia Code", "Fira Code", monospace',
                  fontSize: '0.8rem', lineHeight: 1.6, whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all', bgcolor: '#1e293b', color: '#e2e8f0', minHeight: '100%',
                }}
              >
                {fileContent || 'No content'}
              </Box>
            )}
          </Box>
        </Paper>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </Box>
  );
};

export default ReportLogs;
